<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title>
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon>
          </v-btn>
          Payment Receipt Details
        </v-card-title>
        <v-card-text>
          <v-progress-linear
            v-if="isLoading"
            indeterminate
            color="primary"
          ></v-progress-linear>
          <div v-else-if="receipt">
            <v-row>
              <v-col sm="6">
                <v-card outlined class="pa-4">
                  <h4 class="mb-3">Receipt Information</h4>
                  <v-row>
                    <v-col cols="4"><strong>Receipt No:</strong></v-col>
                    <v-col cols="8">{{ receipt.receipt_no || 'N/A' }}</v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="4"><strong>Amount Received:</strong></v-col>
                    <v-col cols="8">₹{{ receipt.amount_received || '0.00' }}</v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="4"><strong>Status:</strong></v-col>
                    <v-col cols="8">
                      <v-chip
                        small
                        :color="getStatusColor(receipt.code)"
                        text-color="white"
                      >
                        {{ getStatusText(receipt.code) }}
                      </v-chip>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="4"><strong>Payment Date:</strong></v-col>
                    <v-col cols="8">{{ formatDate(receipt.date_of_payment) }}</v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="4"><strong>Created At:</strong></v-col>
                    <v-col cols="8">{{ formatDateTime(receipt.created_at) }}</v-col>
                  </v-row>
                </v-card>
              </v-col>
              <v-col sm="6">
                <v-card outlined class="pa-4">
                  <h4 class="mb-3">Payment Details</h4>
                  <v-row>
                    <v-col cols="4"><strong>Payment Type:</strong></v-col>
                    <v-col cols="8">{{ receipt.type || 'N/A' }}</v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="4"><strong>Transaction ID:</strong></v-col>
                    <v-col cols="8">{{ receipt.transaction_id || 'N/A' }}</v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="4"><strong>UTR:</strong></v-col>
                    <v-col cols="8">{{ receipt.utr || 'N/A' }}</v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="4"><strong>Account Type:</strong></v-col>
                    <v-col cols="8">{{ receipt.account_type || 'N/A' }}</v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="4"><strong>Contact Number:</strong></v-col>
                    <v-col cols="8">{{ receipt.contact_number || 'N/A' }}</v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="4"><strong>Mode of Payment:</strong></v-col>
                    <v-col cols="8">{{ getModeText(receipt.mode_of_payment) }}</v-col>
                  </v-row>
                </v-card>
              </v-col>
            </v-row>
            <v-row v-if="receipt.user">
              <v-col sm="12">
                <v-card outlined class="pa-4">
                  <h4 class="mb-3">User Information</h4>
                  <v-row>
                    <v-col cols="2"><strong>Name:</strong></v-col>
                    <v-col cols="4">{{ receipt.user.first_name }} {{ receipt.user.last_name || '' }}</v-col>
                    <v-col cols="2"><strong>Email:</strong></v-col>
                    <v-col cols="4">{{ receipt.user.email || 'N/A' }}</v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="2"><strong>Phone:</strong></v-col>
                    <v-col cols="4">{{ receipt.user.phone || 'N/A' }}</v-col>
                    <v-col cols="2"><strong>Address:</strong></v-col>
                    <v-col cols="4">{{ receipt.user.address || 'N/A' }}</v-col>
                  </v-row>
                </v-card>
              </v-col>
            </v-row>
          </div>
          <div v-else class="text-center">
            <v-alert type="error" outlined>
              Receipt not found
            </v-alert>
          </div>
        </v-card-text>
      </v-card>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      isLoading: false,
      receipt: null,
    };
  },
  mounted() {
    this.getReceiptDetails();
  },
  methods: {
    async getReceiptDetails() {
      this.isLoading = true;
      try {
        const receiptId = this.$route.params.receiptId;
        const response = await axios.get(`payment_receipts/${receiptId}`);
        
        if (response.data.success) {
          this.receipt = response.data.data;
        } else {
          this.$toast.error("Receipt not found");
        }
      } catch (error) {
        console.error("Error fetching receipt details:", error);
        this.$toast.error("Failed to fetch receipt details");
      } finally {
        this.isLoading = false;
      }
    },
    getStatusColor(code) {
      switch (code) {
        case "PAYMENT_SUCCESS":
          return "green";
        case "PAYMENT_PENDING":
          return "orange";
        case "PAYMENT_FAILED":
          return "red";
        default:
          return "grey";
      }
    },
    getStatusText(code) {
      switch (code) {
        case "PAYMENT_SUCCESS":
          return "Success";
        case "PAYMENT_PENDING":
          return "Pending";
        case "PAYMENT_FAILED":
          return "Failed";
        default:
          return "Unknown";
      }
    },
    getModeText(mode) {
      switch (mode) {
        case 1:
          return "Online";
        case 2:
          return "Offline";
        case 3:
          return "Cash";
        default:
          return "N/A";
      }
    },
    formatDate(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-IN", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
    formatDateTime(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleString("en-IN", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
};
</script>

<style scoped>
.v-card-title {
  font-size: 1.5rem;
  font-weight: bold;
}
</style>